import {
  Building2,
  CalendarCheck,
  FilePlus2,
  Headset,
  MoonStar,
  Scale,
  Stethoscope,
  Target,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import type { AgentTemplateCode } from '@/api/aiAgents'
import { useTranslation } from 'react-i18next'

export type AgentModelId = 'blank' | 'reception' | 'law_firm' | AgentTemplateCode

/**
 * The starting points of the creation assistant (templates are only offered when
 * creating an agent). A new one is a line here, its texts, and its server template.
 */
export const AGENT_MODELS: { id: AgentModelId; icon: LucideIcon; key: string }[] = [
  { id: 'blank', icon: FilePlus2, key: 'blank' },
  { id: 'reception', icon: Headset, key: 'reception' },
  { id: 'law_firm', icon: Scale, key: 'lawFirm' },
  { id: 'rendez_vous', icon: CalendarCheck, key: 'appointments' },
  { id: 'clinique', icon: Stethoscope, key: 'clinic' },
  { id: 'domicile', icon: Wrench, key: 'homeServices' },
  { id: 'immobilier', icon: Building2, key: 'property' },
  { id: 'apres_heures', icon: MoonStar, key: 'afterHours' },
  { id: 'prospects', icon: Target, key: 'leads' },
]

/** Is this one of the templates of services/modeles_agents (no settings, one click)? */
export const isAgentTemplateCode = (id: AgentModelId): id is AgentTemplateCode =>
  !['blank', 'reception', 'law_firm'].includes(id)

interface Props {
  models: typeof AGENT_MODELS
  onPick: (id: AgentModelId) => void
}

/** Small cards, a name and one line each: room for many templates. */
export function AgentModelGallery({ models, onPick }: Props) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {models.map(({ id, icon: Icon, key }) => (
        <button
          key={id}
          type="button"
          onClick={() => onPick(id)}
          className="group flex flex-col items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-left hover:border-blue-400 hover:bg-blue-50/50 dark:hover:border-blue-500 dark:hover:bg-blue-500/10 transition-colors"
        >
          <span className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
            <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </span>
          <span className="text-sm font-semibold text-slate-900 dark:text-white">{t(`manager.aiAgents.models.${key}.title`)}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400 leading-snug">{t(`manager.aiAgents.models.${key}.line`)}</span>
        </button>
      ))}
    </div>
  )
}
